import {
  AlignmentType,
  Paragraph as DocxParagraph,
  IIndentAttributesProperties,
  IParagraphOptions,
  ISpacingProperties,
  PageBreak,
  Run,
  TextRun,
} from "docx";
import { Paragraph } from "~/types/Paragraph";
import { RevisionAnnotations } from "~/types/RevisionAnnotation";
import { useTokenTextRun } from "./useTokenTextRun";
import { useWordFontSize } from "./useWordFontSize";

export const useWordParagraphGenerator = (
  paragraph: Paragraph,
  numberingConfig: { [key: string]: any },
  annotationMap: { [key: number]: RevisionAnnotations },
  annotationParagraphMap: { [key: string]: RevisionAnnotations },
  endLineAnnotations: { [key: string]: RevisionAnnotations },
  previousParagraphCollectionRef: string | undefined,
  commentColor: number[],
  isOSS: boolean
) => {
  const deferredAnnotations: RevisionAnnotations = [];

  // paragraph.formatting.
  let assignmentParagraphs = [];

  if (
    isOSS &&
    paragraph.collectionRef == "paragraphs" &&
    paragraph.localIndex == 0
  ) {
    assignmentParagraphs.push(
      new DocxParagraph({
        children: [
          new PageBreak(),
          new TextRun({ text: "Writing Assignment", size: 36, bold: true }),
        ],
      })
    );

    assignmentParagraphs.push(new DocxParagraph(""));
  }

  const paragraphTokenGroups = useConvertTokensToGroups(paragraph.tokens);

  const spacing: ISpacingProperties = {
    line: paragraph.formatting.lineSpacing >= 2 ? 500 : 250,
  };

  let alignment: AlignmentType = AlignmentType.LEFT;

  switch (paragraph.formatting.alignment) {
    case "CENTER":
      alignment = AlignmentType.CENTER;
      break;
    case "RIGHT":
      alignment = AlignmentType.RIGHT;
      break;
    case "JUSTIFIED":
      alignment = AlignmentType.JUSTIFIED;
      break;
    case "LEFT":
    default:
      alignment = AlignmentType.LEFT;
  }

  const indent: IIndentAttributesProperties | undefined = {
    firstLine:
      paragraph.formatting.firstLineIndent &&
      paragraph.formatting.firstLineIndent > 0
        ? `${paragraph.formatting.firstLineIndent}pt`
        : undefined,
    hanging:
      paragraph.formatting.firstLineIndent &&
      paragraph.formatting.firstLineIndent < 0
        ? `${Math.abs(paragraph.formatting.firstLineIndent)}pt`
        : undefined,
    left: paragraph.formatting.leftIndent
      ? `${paragraph.formatting.leftIndent}pt`
      : undefined,
  };

  for (const tokenGroup of paragraphTokenGroups) {
    const textRuns: Run[] = [];
    for (const token of tokenGroup) {
      if (token.type == "page_break") {
        textRuns.push(new PageBreak());

        continue;
      }

      const fontSize: number = (token.formatting?.size ?? 12) * 2;

      const textRun = useTokenTextRun(token);

      let preAnnotationRuns: TextRun[] = [];
      let postAnnotationRuns: TextRun[] = [];

      const idAnnotations = annotationMap[token.globalPosition];

      if (idAnnotations) {
        for (var an of idAnnotations) {
          if (an) {
            // @ts-ignore
            let annotationTextRuns = useConvertQuillDeltaToTextRuns(
              an.commentObject,
              fontSize,
              useRGBToHex(commentColor),
              false
            ).flat();

            if (useIsAnnotationCommentLong(an)) {
              deferredAnnotations.push(an);
              annotationTextRuns = [
                new TextRun({
                  text: "See below.",
                  color: useRGBToHex(commentColor),
                  size: fontSize,
                }),
              ];
            }

            if (token.type == "filler" || token.type == "space") {
              annotationTextRuns.push(
                new TextRun({
                  text: " ",
                  color: useRGBToHex(commentColor),
                  size: fontSize,
                })
              );
            } else {
              // add a ext run to the beginning of the annotation text runs
              annotationTextRuns = [
                new TextRun({
                  text: " ",
                  color: useRGBToHex(commentColor),
                  size: fontSize,
                }),
                ...annotationTextRuns,
              ];
            }

            // If the annotation is only attached to one token then we put the annotation before the token
            // Other wise we'll render the annotations after the last token

            // IEW requested annotations always come after so I'm doing htat here but will leae in the old code.
            if (an.isStartLine != true) {
              postAnnotationRuns.push(...annotationTextRuns);
            }

            if (an.isStartLine == true) {
              preAnnotationRuns.push(
                ...annotationTextRuns,
                new TextRun({
                  text: " ",
                  color: useRGBToHex(commentColor),
                  size: fontSize,
                })
              );
            }

            // } else {
            //   preAnnotationRuns.push(annotationRun);
            // }
          }
        }
      }

      const indexOfToken = tokenGroup.indexOf(token);
      const isLastTokenInGroup = indexOfToken == tokenGroup.length - 1;

      const paragraphEndLineAnnotations = endLineAnnotations[paragraph.id];

      if (isLastTokenInGroup && paragraphEndLineAnnotations) {
        for (var an of paragraphEndLineAnnotations) {
          if (an) {
            // If the annotation is attached to the last token of a particular group and
            // that value doesn't match this last token, skip over attachign this annotation.
            if (
              an.lastTokenGlobalPosition &&
              an.lastTokenGlobalPosition != token.globalPosition
            ) {
              continue;
            }

            // @ts-ignore
            const annotationTextRuns = useConvertQuillDeltaToTextRuns(
              an.commentObject,
              fontSize,
              useRGBToHex(commentColor),
              false
            ).flat();

            postAnnotationRuns.push(...annotationTextRuns);

            // Push an empty space
            postAnnotationRuns.push(
              new TextRun({
                text: " ",
                color: useRGBToHex(commentColor),
                size: fontSize,
              })
            );
          }
        }
      }

      if (preAnnotationRuns.length > 0) {
        textRuns.push(...preAnnotationRuns);
      }

      textRuns.push(textRun);

      if (postAnnotationRuns.length > 0) {
        textRuns.push(...postAnnotationRuns);
      }

      preAnnotationRuns = [];
      postAnnotationRuns = [];
    }

    let paragraphOptions: IParagraphOptions = { children: textRuns };

    let hasListConfig = false;

    if (paragraph.formatting.listItemInfo) {
      hasListConfig =
        numberingConfig.filter(
          (nc: any) => nc.reference == paragraph.formatting.listItemInfo.listId
        ).length > 0;
    }

    if (
      paragraph.formatting.isList &&
      paragraph.formatting.listItemInfo &&
      hasListConfig == true
    ) {
      paragraphOptions = {
        ...paragraphOptions,
        numbering: {
          reference: paragraph.formatting.listItemInfo.listId,
          level: paragraph.formatting.listItemInfo.nestingLevel,
        },
        leftTabStop: 500 * (paragraph.formatting.listItemInfo.nestingLevel + 1),
        alignment,
      };
    } else {
      paragraphOptions = {
        ...paragraphOptions,
        alignment,
        indent,
        spacing,
      };
    }

    assignmentParagraphs.push(new DocxParagraph(paragraphOptions));

    // Here we insert any deffered annotations or paragraph annotaitons between token groups as necessary
    const paragraphId = paragraph.id;

    const paragraphAnnotations = annotationParagraphMap[paragraphId] ?? [];

    for (const deferredAnnotation of deferredAnnotations) {
      deferredAnnotation.tokenGlobalPositions.sort();

      if (deferredAnnotation.tokenGlobalPositions.length == 0) continue;

      const lastTokenPosition =
        deferredAnnotation.tokenGlobalPositions[
          deferredAnnotation.tokenGlobalPositions.length - 1
        ];

      const indexInGroup = tokenGroup.findIndex(
        (t) => t.globalPosition == lastTokenPosition
      );

      // If the deferred annotqtion is not linked to this particualr group then we need to skip
      if (indexInGroup < 0) continue;

      const deferredAnnotationRuns: TextRun[] = [];

      const tokenText = deferredAnnotation.tokenText;

      if (tokenText != "") {
        const tokenTextAnnotationRun = new TextRun({
          size: 24,
          text: `${tokenText} - `,
          color: useRGBToHex(commentColor),
          bold: true,
        });

        deferredAnnotationRuns.push(tokenTextAnnotationRun);
      }

      const annotationTextRuns = useConvertQuillDeltaToTextRuns(
        deferredAnnotation.commentObject,
        useWordFontSize(),
        useRGBToHex(commentColor),
        true
      ).flat();

      var index = 0;

      for (const annotationTextRun of annotationTextRuns) {
        let defaultBreak = 2;

        if (index == 0 && spacing.line == 500) {
          defaultBreak = 0;
        } else if (index == 0 && spacing.line == 250) {
          defaultBreak = 1;
        }

        deferredAnnotationRuns.push(annotationTextRun);
        // deferredAnnotationRuns.push(new TextRun({ break: defaultBreak }));

        index++;
      }

      deferredAnnotationRuns.push(new TextRun({ break: 1 }));

      assignmentParagraphs.push(
        new DocxParagraph({
          children: deferredAnnotationRuns,
          alignment: AlignmentType.LEFT,
          spacing: {
            line: 250,
          },
        })
      );
    }

    const preParagraphAnnotationRuns = [];
    const postParagraphAnnotationRuns = [];
    let paragraphAnnotationRuns = [];

    for (var an of paragraphAnnotations) {
      // if we have a lasttokenglobalPosition then we need to check if it's in this group
      // If the lastTokenglobalposition for this annotation isn't in this group then we skip it.
      if (an.lastTokenGlobalPosition) {
        var index = tokenGroup.findIndex(
          (t) => t.globalPosition == an.lastTokenGlobalPosition
        );

        if (index < 0) continue;
      }

      const annotationTextRuns = useConvertQuillDeltaToTextRuns(
        an.commentObject,
        useWordFontSize(),
        useRGBToHex(commentColor),
        true
      );

      var index = 0;

      paragraphAnnotationRuns.push(new TextRun({ break: 1 }));

      for (const runs of annotationTextRuns) {
        paragraphAnnotationRuns.push(...runs);

        paragraphAnnotationRuns.push(new TextRun({ break: 1 }));

        index++;
      }

      if (an.isBeforeParagraph == true) {
        preParagraphAnnotationRuns.push(...paragraphAnnotationRuns);
      } else {
        postParagraphAnnotationRuns.push(...paragraphAnnotationRuns);
      }

      paragraphAnnotationRuns = [];
    }

    if (preParagraphAnnotationRuns.length > 0) {
      assignmentParagraphs = [
        new DocxParagraph({
          children: preParagraphAnnotationRuns,
          alignment: AlignmentType.LEFT,
          spacing: {
            line: 250,
          },
        }),
        ...assignmentParagraphs,
      ];
    }

    if (postParagraphAnnotationRuns.length > 0) {
      assignmentParagraphs.push(
        new DocxParagraph({
          children: postParagraphAnnotationRuns,
          alignment: AlignmentType.LEFT,
          spacing: {
            line: 250,
          },
        })
      );
    }

    // End of tokenGroups loop
  }

  return assignmentParagraphs;
};
